import { Shield, Ticket } from 'lucide-react';
import { useState } from 'react';

import {
  getClubeControllerVitrineQueryKey,
  useClubeControllerResgatar,
  useClubeControllerVitrine,
} from '@/api/generated/clube/clube';
import { CardDeCupom } from '@/features/clube/components/CardDeCupom';
import { CardDeRanque } from '@/features/clube/components/CardDeRanque';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { cn } from '@/shared/lib/cn';
import { Skeleton } from '@/shared/ui/skeleton';
import { useQueryClient } from '@tanstack/react-query';

const TODOS = 'Todos';

/**
 * Clube Hormonalle (F5-6).
 *
 * ⚠️ **O filtro por ranque é do SERVIDOR, e o de categoria também.** A tela da
 * V1 tinha os seis cupons hard-coded e filtrava no array em memória; aqui, um
 * cupom acima do nível do médico nem chega no navegador — é a condição
 * comercial do parceiro, e escondê-la no cliente a deixaria a um F12 de
 * distância. As categorias vêm calculadas sobre o conjunto visível inteiro
 * (mesma razão de `ParceirosPage`: derivar do que já carregou faz escolher um
 * chip apagar os outros).
 */
export function ClubePage() {
  const [categoria, setCategoria] = useState<string>(TODOS);
  const queryClient = useQueryClient();

  const { data, isPending, isError, refetch } = useClubeControllerVitrine(
    categoria === TODOS ? undefined : { categoria },
  );

  const resgatar = useClubeControllerResgatar({
    mutation: {
      // Sem toast de sucesso: o feedback já é o ícone de check no botão. O
      // resgate é um efeito colateral do copiar, não uma ação que o médico
      // pediu — anunciá-lo confundiria "copiei" com "usei no parceiro".
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getClubeControllerVitrineQueryKey() });
      },
    },
  });

  if (isError) return <EstadoErro aoTentarDeNovo={() => void refetch()} />;

  const categorias = [TODOS, ...(data?.categorias ?? [])];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Shield className="w-7 h-7 text-brand" />
          Clube Hormonalle
        </h2>
        <p className="text-muted-foreground mt-1">
          Acompanhe sua categoria no Clube Hormonalle e descubra benefícios exclusivos para você.
        </p>
      </div>

      {/* Linha inteira, não coluna (ajuste de Ago/2026): a escada
          Standard→Platinum do card só cabe legível na horizontal. */}
      {isPending ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : (
        <CardDeRanque ranque={data.ranque} />
      )}

      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {categorias.map((opcao) => (
            <button
              key={opcao}
              type="button"
              onClick={() => setCategoria(opcao)}
              aria-pressed={categoria === opcao}
              className={cn(
                'h-8 px-3 rounded-lg text-xs font-medium border transition-colors',
                categoria === opcao
                  ? 'bg-brand text-brand-foreground border-brand'
                  : 'bg-transparent text-foreground border-border hover:bg-muted',
              )}
            >
              {opcao}
            </button>
          ))}
        </div>

        {isPending ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-64 w-full rounded-xl" />
            ))}
          </div>
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={Ticket}
            titulo="Nenhum cupom por aqui"
            descricao={
              categoria === TODOS
                ? 'Assim que houver cupons para o seu nível, eles aparecem aqui.'
                : `Não há cupons na categoria "${categoria}".`
            }
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.itens.map((cupom) => (
              <CardDeCupom
                key={cupom.id}
                cupom={cupom}
                aoCopiar={(id) => resgatar.mutate({ id })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
